import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import useSnackbar from '../../hooks/useSnackbar';

export interface QuotationContextField {
  label: string;
  value: string;
}

interface QuotationModalProps {
  open: boolean;
  onClose: () => void;
  /** Read-only context shown above the form (e.g. Customer, Property Category). */
  contextFields?: QuotationContextField[];
  /** Performs the actual API call — thrown errors are caught and snackbar'd here. */
  onSubmit: (payload: { amount: number; notes: string }) => Promise<void>;
  /** Called after a successful submit, once the modal has closed itself. */
  onSubmitted?: () => void;
  title?: string;
  submitLabel?: string;
}

const ReadOnly = ({ label, value }: QuotationContextField) => (
  <Box>
    <Typography sx={{ color: '#9e9e9e', fontSize: 13, mb: 0.5 }}>
      {label}
    </Typography>
    <Typography sx={{ color: '#fff', fontSize: 15 }}>{value || 'N/A'}</Typography>
  </Box>
);

const darkInputSx = {
  '& .MuiOutlinedInput-root': {
    color: '#fff',
    backgroundColor: '#1c1c1c',
    borderRadius: '8px',
    '& fieldset': { borderColor: '#5a5a5a' },
    '&:hover fieldset': { borderColor: '#8a8a8a' },
  },
  '& .MuiInputBase-input::placeholder': { color: '#9e9e9e', opacity: 1 },
};

/**
 * Generic "share a quotation" dialog used across every request type (AMC,
 * Sell Old Boiler, Expert Advice, Site Visit). Presentational only — each
 * call site supplies its own read-only context fields and its own
 * type-specific submit function (each type has its own quotation endpoint
 * and its own storage model, so this component never talks to the network
 * directly).
 */
export const QuotationModal: React.FC<QuotationModalProps> = ({
  open,
  onClose,
  contextFields = [],
  onSubmit,
  onSubmitted,
  title = 'Add Quotation',
  submitLabel = 'Share Quotation',
}) => {
  const { showSnackbar, SnackbarComponent } = useSnackbar();
  const [amount, setAmount] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setAmount('');
      setNotes('');
    }
  }, [open]);

  const handleSubmit = async () => {
    const numericAmount = Number(amount);
    if (!amount || isNaN(numericAmount) || numericAmount <= 0) {
      showSnackbar('Enter a valid quotation amount', 'error');
      return;
    }

    try {
      setSubmitting(true);
      await onSubmit({ amount: numericAmount, notes });
      showSnackbar('Quotation shared successfully', 'success');
      onClose();
      onSubmitted?.();
    } catch (err) {
      showSnackbar(
        err instanceof Error ? err.message : 'Failed to submit quotation',
        'error',
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={() => !submitting && onClose()}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            background: '#1a1a1a',
            borderRadius: '20px',
            border: '1px solid #7B1FA2',
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            color: '#fff',
          }}
        >
          {title}
          <IconButton onClick={() => !submitting && onClose()} size="small">
            <CloseIcon sx={{ color: '#fff' }} />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ pt: 1 }}>
          {contextFields.length > 0 && (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 2.5,
                mb: 3,
              }}
            >
              {contextFields.map((field) => (
                <ReadOnly key={field.label} {...field} />
              ))}
            </Box>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <TextField
              placeholder="Quotation Amount (£)"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              type="number"
              fullWidth
              variant="outlined"
              sx={darkInputSx}
            />
            <TextField
              placeholder="Notes (optional)"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              fullWidth
              multiline
              minRows={2}
              variant="outlined"
              sx={darkInputSx}
            />
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            onClick={handleSubmit}
            disabled={submitting}
            fullWidth
            sx={{
              background: '#7B1FA2',
              color: '#fff',
              py: 1.4,
              fontSize: 16,
              borderRadius: '14px',
              fontWeight: 600,
              '&:hover': { background: '#5E167A' },
              '&.Mui-disabled': { background: '#4a2557', color: '#bbb' },
            }}
          >
            {submitting ? 'Sharing…' : submitLabel}
          </Button>
        </DialogActions>
      </Dialog>
      {SnackbarComponent}
    </>
  );
};

export default QuotationModal;
